/**
 * Reconnect policy for the fetch-based SSE clients.
 *
 * `@microsoft/fetch-event-source` retries *forever* at a fixed 1s interval when
 * no `onerror` is supplied — so a permanently-broken endpoint (CORS misconfig,
 * dead ALB target, 401/403) reconnects every second indefinitely (the
 * runaway-loop bug). This policy bounds that:
 *
 *   - `onopen` stops immediately on a 4xx status (auth/forbidden/not-found never
 *     recover by reconnecting); a 5xx falls through to the backoff path.
 *   - `onerror` applies capped exponential backoff and gives up after
 *     `MAX_RETRIES` consecutive failures, so the loop dies instead of hammering.
 *   - a successful open resets the counter, so an occasionally-flaky-but-healthy
 *     stream keeps reconnecting indefinitely (only *consecutive* failures count).
 *
 * Browser note: a CORS preflight failure and a network outage are
 * indistinguishable (both surface as `TypeError: Failed to fetch` with no
 * `Response`), so neither can be singled out — both take the transient backoff
 * path and are bounded by `MAX_RETRIES`. Once the policy gives up, live push
 * stays down until the subscription is re-created (e.g. the panel remounts).
 */

const BASE_RETRY_MS = 1_000;
/** Give up after this many consecutive failed connection attempts. */
const MAX_RETRIES = 6;

/**
 * Non-retriable failure marker. Re-thrown from `onerror` so `fetchEventSource`
 * rejects (and stops) instead of scheduling another reconnect.
 */
class FatalStreamError extends Error {
  constructor(status: number) {
    super(`stream refused: HTTP ${String(status)}`);
    this.name = 'FatalStreamError';
  }
}

export interface SseRetryPolicy {
  /** Pass as `fetchEventSource`'s `onopen`. Throws a fatal error on a 4xx. */
  onopen: (response: Response) => Promise<void>;
  /** Pass as `fetchEventSource`'s `onerror`. Returns the backoff in ms, or throws to stop. */
  onerror: (err: unknown) => number;
}

/** One policy instance per subscription — the failure counter is per-connection. */
export function createSseRetryPolicy(): SseRetryPolicy {
  let failures = 0;
  return {
    onopen: (response) => {
      if (response.ok) {
        failures = 0; // healthy connection — reset the backoff window
        return Promise.resolve();
      }
      // 4xx: client-side refusal (auth/forbidden/not-found). Reconnecting can't
      // fix it — stop the loop now.
      if (response.status >= 400 && response.status < 500) {
        return Promise.reject(new FatalStreamError(response.status));
      }
      // 5xx (and other non-ok): transient — let `onerror` apply backoff.
      return Promise.reject(new Error(`stream open failed: HTTP ${String(response.status)}`));
    },
    onerror: (err) => {
      if (err instanceof FatalStreamError) throw err; // non-retriable — stop
      failures += 1;
      if (failures >= MAX_RETRIES) throw err; // give up — kills the reconnect loop
      return BASE_RETRY_MS * 2 ** (failures - 1);
    },
  };
}
